import Tooltips from 'components/tooltips/Tooltips';
import useText from 'hooks/useText';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import EmployeeAvatar from './EmployeeAvatar';

const MENU_WIDTH = 192;
const OPTION =
  'flex w-full items-center gap-2 rounded-md px-4 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-100 active:bg-gray-200';

/**
 * Who something is assigned to, and the list that changes it, as on the
 * mission cards: the avatar, then the employees in a menu drawn in <body> (no
 * scrolling list cuts it), opening up when there is no room below.
 * `value` and `onChange` work with employee ids; `meId` marks yourself.
 * `allowUnassigned` offers « Non assigné » first (the mission cards);
 * `emptyLabel` says what the empty avatar assigns (« Attribuer » by default).
 * `options` narrows the list to some of the employees (all by default): the
 * holder is still looked up among all of them, so one who can no longer be
 * chosen (a role changed since) still shows as assigned.
 */
const AssignMenu = ({ value, employees, options = employees, meId, onChange, allowUnassigned = false, emptyLabel }) => {
  const { text } = useText('assign');
  const assignLabel = emptyLabel ?? text('tooltip');
  const [box, setBox] = useState(null);
  const trigger = useRef(null);
  const menu = useRef(null);
  const current = employees.find((employee) => employee.id === value) ?? null;

  useEffect(() => {
    if (!box) return undefined;
    const close = (event) => {
      if (!trigger.current?.contains(event.target) && !menu.current?.contains(event.target)) setBox(null);
    };
    const hide = () => setBox(null);
    const onKey = (event) => event.key === 'Escape' && setBox(null);
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [box]);

  const toggle = (event) => {
    event.stopPropagation();
    if (box) return setBox(null);
    const rect = trigger.current.getBoundingClientRect();
    const left = Math.min(rect.left, window.innerWidth - MENU_WIDTH - 8);
    const below = window.innerHeight - rect.bottom > 270;
    return setBox(below ? { top: rect.bottom + 4, left } : { bottom: window.innerHeight - rect.top + 4, left });
  };
  const choose = (event, id) => {
    event.stopPropagation();
    setBox(null);
    if (id !== value) onChange(id);
  };
  const name = (employee) => {
    const full = `${employee.firstname} ${employee.lastname}`;
    return employee.id === meId ? text('me', { name: full }) : full;
  };

  return (
    <>
      <Tooltips text={current ? name(current) : assignLabel} placement="top" disabled={Boolean(box)}>
        <button
          ref={trigger}
          type="button"
          aria-label={assignLabel}
          aria-haspopup="menu"
          aria-expanded={Boolean(box)}
          onClick={toggle}
          className="flex rounded-full transition-shadow hover:ring-2 hover:ring-gray-200"
        >
          <EmployeeAvatar employee={current} />
        </button>
      </Tooltips>
      {box &&
        createPortal(
          <div
            ref={menu}
            role="menu"
            style={{ ...box, width: MENU_WIDTH }}
            onClick={(event) => event.stopPropagation()}
            className="fixed z-[99999] max-h-64 overflow-y-auto rounded-lg border border-gray-100 bg-white py-1 shadow-lg"
          >
            {allowUnassigned && (
              <button
                type="button"
                role="menuitem"
                onClick={(event) => choose(event, null)}
                className={`${OPTION} ${!value ? 'bg-gray-50' : ''}`}
              >
                <EmployeeAvatar employee={null} />
                <span>{text('unassigned')}</span>
              </button>
            )}
            {options.map((employee) => (
              <button
                key={employee.id}
                type="button"
                role="menuitem"
                onClick={(event) => choose(event, employee.id)}
                className={`${OPTION} ${employee.id === value ? 'bg-gray-50' : ''}`}
              >
                <EmployeeAvatar employee={employee} />
                <span className="truncate">{name(employee)}</span>
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
};

export default AssignMenu;
